import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface ScoreHeroProps {
  score: number;
  label: string;
  viability: string;
  targetAmount?: number;
  className?: string;
  appliedBonus?: number;
}

export const ScoreHero = ({ score, label, viability, targetAmount, className, appliedBonus = 0 }: ScoreHeroProps) => {
  const formatLabel = (text: string) => {
    return text
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      notation: "compact",
      compactDisplay: "short"
    }).format(amount);
  };

  // Helper to determine color based on score
  const getScoreColor = (value: number) => {
    if (value >= 80) return "text-green-600";
    if (value >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const scoreColor = getScoreColor(score);
  
  // Circle circumference for SVG stroke-dasharray
  // r=54, C = 2 * pi * r = 2 * 3.14159 * 54 ≈ 339.292
  const circumference = 339.292;
  const strokeDashoffset = circumference - (circumference * score) / 100;

  return (
    <div className={cn("w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-4", className)}>
      {/* Project Info Card */}
      <div className="md:col-span-2 p-6 md:p-8 bg-card border border-border rounded-2xl flex flex-col justify-between animate-slide-up relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-secondary to-transparent opacity-50 rounded-bl-[100px] -z-0 pointer-events-none"></div>
        <div className="flex items-start gap-6 relative z-10">
          <div className="w-20 h-20 relative shrink-0 hidden sm:block">
            <img
              src="https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?q=80&w=400&auto=format&fit=crop"
              alt="Artie"
              className="w-full h-full rounded-full object-cover border-2 border-background shadow-sm"
            />
            <div className="absolute -right-2 -bottom-1 bg-primary text-primary-foreground p-1.5 rounded-full shadow-lg border-2 border-background">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12l5 5l10 -10"></path>
              </svg>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-secondary border border-border">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Analysis Complete
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground tracking-tight font-cabinet mt-1">
              Project Details
            </h1>
            <p className="text-muted-foreground font-ibm text-sm leading-relaxed max-w-md">
              Artie has analyzed your script. The genre, format, and tone have
              been inferred with high confidence.
            </p>
          </div>
        </div>
      </div>

      {/* Score Card (Premium Gauge Design) */}
      <div className="md:col-span-1 p-6 bg-white text-slate-900 rounded-2xl flex flex-col items-center justify-center text-center relative overflow-hidden shadow-sm border border-slate-200 animate-slide-up delay-100 group">
        
        <div className="relative z-10 w-full flex flex-col items-center">
          <div className="flex justify-between w-full items-center mb-4">
            <span className="text-[10px] font-bold text-slate-400 font-ibm uppercase tracking-widest">
              OVERALL SCORE
            </span>
            {score >= 80 && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px] font-bold px-2 py-0.5">
                ELITE
              </Badge>
            )}
          </div>

          <div className="relative w-40 h-40 my-2">
            {/* Outer Decorative Ring */}
            <div className="absolute inset-0 rounded-full border-[1px] border-slate-100 scale-110"></div>
            
            <svg className="w-full h-full transform -rotate-90">
              {/* Background Track */}
              <circle
                cx="80"
                cy="80"
                r="70"
                stroke="#F1F5F9"
                strokeWidth="12"
                fill="transparent"
              ></circle>
              {/* Active Score Arc */}
              <circle
                cx="80"
                cy="80"
                r="70"
                stroke={score >= 80 ? "#10B981" : score >= 60 ? "#F59E0B" : "#4F46E5"}
                strokeWidth="12"
                fill="transparent"
                strokeDasharray="439.8"
                strokeDashoffset={439.8 - (439.8 * score) / 100}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-out"
              ></circle>
            </svg>
            
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <span className="text-4xl font-bold font-cabinet tracking-tight flex items-center justify-center">
                {appliedBonus > 0 ? (
                  <>
                    <span>{score - appliedBonus}</span>
                    <span className="text-lg text-green-500 ml-1 font-bold">+{appliedBonus}</span>
                  </>
                ) : (
                  score
                )}
              </span>
              <span className="text-xs text-slate-400 font-medium -mt-1">/100</span>
            </div>
          </div>

          <div className="mt-4 text-center w-full">
            <div className="w-full h-px bg-slate-100 mb-4"></div>
            <h3 className="font-bold font-cabinet text-lg text-slate-800">
              {formatLabel(label)}
            </h3>
            <p className="text-xs text-slate-500 font-ibm mt-1">
              {viability === 'achievable' ? (
                <span className="flex items-center justify-center gap-1.5 text-green-600 font-medium">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  Target Achievable
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1.5 text-yellow-600 font-medium">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="2" y2="22"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                  {formatLabel(viability)}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};